import type { ShopperProducts } from '@/scapi';
import { getDisplayAts } from '@/lib/sold-by-weight';

/**
 * Represents a selected child product with its variant and quantity
 */
export interface ChildProductSelection {
    product: ShopperProducts.schemas['Product'];
    variant?: ShopperProducts.schemas['Variant'];
    quantity: number;
}

/**
 * Returns whether the site-level inventory is out of stock for the given product and quantity.
 *
 * A product is considered out of stock if:
 * - It is not orderable, OR
 * - The available to sell (ats) is less than the requested quantity
 *
 * Sets/Bundles Inventory Pre-calculation (PWA Kit approach):
 * - For sets: Calculate how many complete sets can be made from child inventory
 *   Formula: availableSets = Math.floor(childStockLevel / childQuantity) for each child, use minimum
 *   Example: If set requires 2x shirts (stock=10) and 1x pants (stock=3), you can make min(10/2, 3/1) = 3 complete sets
 * - For bundles: Use bundle's own inventory directly (no calculation needed)
 * - This pre-calculation should be done BEFORE calling this function
 *
 * Variant precedence:
 * - If variant is provided, variant.inventory takes precedence over product.inventory for site inventory
 * - Returns true if no inventory is found (neither variant.inventory nor product.inventory)
 *
 * @param product - The product to check site inventory for (with pre-calculated inventory for sets)
 * @param quantity - The quantity to check availability for (default: 1)
 * @param variant - Optional variant to use for site inventory (takes precedence over product.inventory)
 * @returns true if product is out of stock at site level, false if in stock or if product is undefined
 */
export function isSiteOutOfStock(
    product: ShopperProducts.schemas['Product'] | undefined,
    quantity: number = 1,
    variant?: ShopperProducts.schemas['Variant'] | null
): boolean {
    if (!product) {
        return false;
    }

    // Site inventory: variant takes precedence over product (already calculated for sets/bundles)
    // Note: TypeScript Variant schema doesn't include inventory, but it exists at runtime (proven by PWA Kit)
    const inventory = (variant as { inventory?: ShopperProducts.schemas['Inventory'] })?.inventory || product.inventory;
    if (!inventory) return true;

    // Prefer ATS from the variant/product display helpers, then the resolved inventory object.
    // Thin `product.variants[]` entries often lack `inventory`; without this fallback ATS becomes 0
    // and flash a false out-of-stock while the PDP still has stock on `product.inventory`.
    const ats = getDisplayAts(variant ?? product) ?? inventory.ats ?? 0;
    const orderable = inventory.orderable ?? false;
    return ats < quantity || !orderable;
}

/**
 * Gets the effective inventory object considering both store and site inventory.
 *
 * Selection logic:
 * - If isPickup is true and storeInventoryId is provided: returns store inventory from product.inventories array
 * - Otherwise: returns site inventory (variant.inventory takes precedence over product.inventory if variant is provided)
 *
 * @param product - The product containing inventory data
 * @param isPickup - Whether store pickup is selected (true) or delivery is selected (false)
 * @param storeInventoryId - The inventory ID of the selected store (required when isPickup is true)
 * @param variant - Optional variant to use for site inventory (takes precedence over product.inventory when isPickup is false)
 * @returns Inventory object if found, or null if not found or if product is undefined
 */
export function getEffectiveInventory({
    product,
    variant,
}: {
    product: ShopperProducts.schemas['Product'];
    variant?: ShopperProducts.schemas['Variant'] | null;
}): ShopperProducts.schemas['Inventory'] | null {
    if (!product) return null;

    // Site inventory: variant takes precedence over product
    // Note: TypeScript Variant schema doesn't include inventory, but it exists at runtime (proven by PWA Kit)
    return (variant as { inventory?: ShopperProducts.schemas['Inventory'] })?.inventory || product.inventory || null;
}

/**
 * Gets the effective stock level considering both store and site inventory.
 *
 * Selection logic:
 * - If isPickup is true and storeInventoryId is provided: returns store inventory stockLevel from product.inventories array
 * - Otherwise: returns site inventory ats (available to sell)
 * - Variant precedence: when using site inventory, variant.inventory takes precedence over product.inventory
 *
 * Sets/Bundles Inventory Pre-calculation (PWA Kit approach):
 * - For sets: Calculate how many complete sets can be made from child inventory
 *   Formula: availableSets = Math.floor(childStockLevel / childQuantity) for each child, use minimum
 *   Example: If set requires 2x shirts (stock=10) and 1x pants (stock=3), you can make min(10/2, 3/1) = 3 complete sets
 * - For bundles: Use bundle's own inventory directly (no calculation needed)
 * - This pre-calculation should be done BEFORE calling this function
 *
 * @param product - The product to get stock level for (with pre-calculated inventory for sets)
 * @param isPickup - Whether store pickup is selected (true) or delivery is selected (false)
 * @param storeInventoryId - The inventory ID of the selected store (required when isPickup is true)
 * @param variant - Optional variant to use for site inventory (takes precedence over product.inventory when isPickup is false)
 * @returns The stock level number (0 if product is undefined, inventory not found, or stock level is unavailable)
 */
export function getEffectiveStockLevel({
    product,
    variant,
}: {
    product: ShopperProducts.schemas['Product'];
    variant?: ShopperProducts.schemas['Variant'] | null;
}): number {
    if (!product) return 0;

    // Use helper function (inventory already calculated for sets/bundles)
    const inventory = getEffectiveInventory({
        product,
        variant,
    });
    return getDisplayAts(variant ?? product) ?? inventory?.ats ?? 0;
}

/**
 * Checks if product is in stock considering both store and site inventory.
 *
 * A product is considered in stock if:
 * - It is orderable, AND
 * - The stock level is greater than or equal to the requested quantity
 *
 * Selection logic:
 * - If isPickup is true and storeInventoryId is provided: checks store inventory (from product.inventories array)
 * - Otherwise: checks site inventory
 * - Variant precedence: when using site inventory, variant.inventory takes precedence over product.inventory
 *
 * Sets/Bundles Inventory Pre-calculation (PWA Kit approach):
 * - For sets: Calculate how many complete sets can be made from child inventory
 *   Formula: availableSets = Math.floor(childStockLevel / childQuantity) for each child, use minimum
 *   Example: If set requires 2x shirts (stock=10) and 1x pants (stock=3), you can make min(10/2, 3/1) = 3 complete sets
 * - For bundles: Use bundle's own inventory directly (no calculation needed)
 * - This pre-calculation should be done BEFORE calling this function
 *
 * Implementation note: This function reuses isStoreOutOfStock and isSiteOutOfStock for consistency,
 * where isInStock = !isOutOfStock.
 *
 * @param product - The product to check inventory for (with pre-calculated inventory for sets)
 * @param isPickup - Whether store pickup is selected (true) or delivery is selected (false)
 * @param storeInventoryId - The inventory ID of the selected store (required when isPickup is true)
 * @param quantity - The quantity to check availability for (default: 1)
 * @param variant - Optional variant to use for site inventory (takes precedence over product.inventory when isPickup is false)
 * @returns true if product is orderable and has sufficient stock, false if out of stock or if product is undefined
 */
export function isInStock({
    product,
    quantity = 1,
    variant,
}: {
    product?: ShopperProducts.schemas['Product'];
    quantity: number;
    variant?: ShopperProducts.schemas['Variant'] | null;
}): boolean {
    if (!product) return false;

    return !isSiteOutOfStock(product, quantity, variant);
}
